import moment, { DurationInputArg1, unitOfTime } from 'moment';
import type { ParamsTimeType } from '../types/common';
import { ServerTimeType, TipsType } from '../components/FilterCommon/types';
import {
  dailyToDay,
  sameDateFormat,
} from '../components/FilterCommon/constants/date';
import { timeZeroGameList } from '@op/constants/specialGame';
import {
  commonUrlDateFilterTips,
  influencerActivityDateFilterTips,
  TipsMapFunType,
  TipsMapType,
  UTCDateFilterTips,
  UTCVideDateFilterTips,
} from '@op/components/BubbleCommon';
import DBO_FWC from '@op/hooks/useFrontWebConfig';
import { DBO_TM } from './format';
export const timeFormatObj: { [key in ParamsTimeType]: string } = {
  daily: 'YYYY-MM-DD',
  weekly: 'YYYY-MM-DD',
  monthly: 'YYYY-MM',
  hourly: 'MM-DD HH:mm',
};

export const allTimeFormatObj: { [key in any]: string } = {
  daily: 'YYYY-MM-DD',
  weekly: 'YYYY-MM-DD',
  monthly: 'YYYY-MM',
  hourly: 'YYYY-MM-DD HH:mm:ss',
};

export let paramsToMomentMap: {
  [key in ParamsTimeType]?: moment.unitOfTime.DurationConstructor;
} = {
  monthly: 'months',
  weekly: 'weeks',
  daily: 'days',
  hourly: 'hours',
};

/**
 * 两段函数，通过dateType获取格式化的格式，返回一个函数
 * 返回函数通过value对其格式化对应格式
 * @param dateType
 * @returns
 */
export const formatDateFunc =
  (dateType: ParamsTimeType = 'daily') =>
    (value: string) =>
      moment(value).format(timeFormatObj[dateType]);

export const serverDateFormatObj: { [key in any]: string } = {
  daily: 'YYYY-MM-DD',
  weekly: 'YYYY-MM-DD',
  monthly: 'YYYY-MM',
  hourly: 'YYYY-MM-DD HH:mm',
};
/**
 * 处理数据里的时间，当为小时的时候保留到小时位置，如果不是小时都保留到日期
 * @param dateType
 * @param value
 * @returns
 */
export const formatDateToServerDataFunc = (
  dateType: ParamsTimeType,
  value: string,
) => {
  return moment(value).format(serverDateFormatObj[dateType]);
};

export const formatFilterDate = (
  value: string,
  { date_type = 'daily' }: { date_type: ParamsTimeType },
) => moment(value).format(timeFormatObj[date_type]);

export const dataGap = (startTime: string, endTime: string) => {
  var diffInDays = moment(endTime).diff(moment(startTime), 'days');
  return diffInDays;
};

export function formatCalendar(
  bDate: string,
  eDate: string,
  calendarType = 'Day',
) {
  const monthDic: any = {
    '01': 'Jan',
    '02': 'Feb',
    '03': 'Mar',
    '04': 'Apr',
    '05': 'May',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Aug',
    '09': 'Sep',
    10: 'Oct',
    11: 'Nov',
    12: 'Dec',
  };
  if (bDate === eDate) {
    eDate = '';
  }
  let bString, year, bMonth, eString, bDay, eDateArr, eMonth, eDay;
  if (bDate && bDate != '') {
    const bDateArr = bDate.split('-');
    year = ', ' + bDateArr[0];
    bMonth = monthDic[bDateArr[1]];
    bDay = String(parseInt(bDateArr[2]));
    bString = `${bMonth}${calendarType === 'Day' || calendarType === 'Week' ? ' ' + bDay : ''
      }`;
  } else {
    bString = '';
    year = '';
  }
  if (eDate && eDate != '') {
    eDateArr = eDate.split('-');
    eMonth = monthDic[eDateArr[1]];
    eDay = String(parseInt(eDateArr[2]));
    eString = ` - ${eMonth}${calendarType === 'Day' || calendarType === 'Week' ? ' ' + eDay : ''
      }`;
    year = ', ' + eDateArr[0];
  } else {
    eString = '';
  }
  return `${bString}${eString}${year}`;
}

/**
 * 时长转换，秒转 xx:xx:xx 时长展示
 * @param value int
 * @returns
 */

export function duration(durationInSeconds: number, defaultValue = '00:00:00') {
  function pad(num: number) {
    const newNum = Math.floor(num);
    return newNum > 9 ? `${newNum}` : `0${newNum}`;
  }

  if (
    typeof durationInSeconds !== 'number' ||
    isNaN(durationInSeconds) ||
    durationInSeconds === Infinity ||
    durationInSeconds <= 0
  ) {
    return defaultValue;
  }
  const leftSeconds = durationInSeconds % 60;
  const minutes = Math.floor(durationInSeconds / 60);
  if (minutes < 60) {
    return `${pad(minutes)}:${pad(leftSeconds)}`;
  }
  const leftMinutes = minutes % 60;
  const hours = Math.floor(minutes / 60);
  return `${pad(hours)}:${pad(leftMinutes)}:${pad(leftSeconds)}`;
}

/**
 * 判断时间是否为同一日，同一周，同一月
 * 当为周的时候： 周一 和 周二都属于同一周
 * @param dateType
 * @returns
 */
export const isSameTime =
  (dateType: ServerTimeType, dailyToDayMap = dailyToDay) =>
    (startTime: string, endTime: string) => {
      return moment(startTime).isSame(endTime, dailyToDayMap[dateType]);
    };

/**
 * 其他系统使用
 * 日期格式化方法
 * @param param0
 * @returns
 */
export function formatTimeToShow(
  {
    start_time,
    end_time,
    date_type = 'daily',
    link_text = '~',
    hourlyIsSame = false,
  }: {
    start_time: string;
    end_time: string;
    date_type: ServerTimeType;
    link_text?: string;
    hourlyIsSame?: boolean;
  } = { start_time: '', end_time: '', date_type: 'daily', link_text: '~' },
) {
  const isSame = isSameTime(date_type, sameDateFormat)(start_time, end_time);
  if (date_type == 'hourly') {
    if (hourlyIsSame && isSame) {
      return moment(start_time).format('YYYY-MM-DD HH:mm');
    }
    return (
      moment(start_time).format('YYYY-MM-DD HH:mm') +
      link_text +
      moment(end_time).format('YYYY-MM-DD HH:mm')
    );
  } else {
    const formatType = timeFormatObj[date_type];
    if (isSame) {
      return moment(start_time).format(formatType);
    } else {
      return (
        moment(start_time).format(formatType) +
        link_text +
        moment(end_time).format(formatType)
      );
    }
  }
}

/**
 * 获取环比的文案
 * @param dateType
 * @param startTime
 * @param endTime
 * @returns
 */
export const getQOQText = (
  dateType: ServerTimeType,
  startTime: string,
  endTime: string,
) => {
  const sameTime = isSameTime(dateType)(
    formatDateFunc(dateType)(startTime),
    formatDateFunc(dateType)(endTime),
  );
  const isSingleDay = dateType === 'daily' && sameTime;
  const isSingleWeek =
    dateType === 'weekly' && dataGap(startTime, endTime) === 6;
  const isSingleMonth = dateType === 'monthly' && sameTime;

  const text = isSingleWeek
    ? 'WoW'
    : isSingleMonth
      ? 'MoM'
      : isSingleDay
        ? 'DoD'
        : 'Change%';

  return {
    sameTime,
    isSingleDay,
    isSingleWeek,
    isSingleMonth,
    isSingleDate: isSingleDay || isSingleWeek || isSingleMonth,
    text,
  };
};

export const getFilterDateQOQText = (filter: {
  date_type: ServerTimeType;
  start_time: string;
  end_time: string;
}) => {
  const { start_time, end_time, date_type } = filter;
  return getQOQText(date_type, start_time, end_time);
};

/**
 * TODO: 其他系统使用
 * 转换时间类型映射值
 * @param date_type
 * @returns
 */
export function getNeedTime(date_type: ServerTimeType) {
  return ['daily', 'weekly'].includes(date_type)
    ? 'days'
    : date_type === 'hourly'
      ? 'hourly'
      : 'year';
}

export function formatSimpleTimeFunc(
  valueTime: string,
  formatText = 'YYYY-MM-DD',
) {
  if (!valueTime) {
    return valueTime;
  }
  let time = moment(valueTime).format(formatText);
  if (time !== 'Invalid date') {
    return time;
  }

  return valueTime;
}

/**
 * 以当前时间为中点，获取一段时间的区间
 * @param time    中点时间
 * @param amount  时间跨度数量
 * @param unit    时间跨度计算的单位
 * @returns
 */
export const getTimeSlot = (
  time: string,
  amount: DurationInputArg1 = 1,
  unit: unitOfTime.DurationConstructor | undefined = 'day',
  format: string = 'YYYY-MM-DD HH:mm:ss',
) => {
  const startTime = moment(time).subtract(amount, unit).format(format);

  if (startTime !== 'Invalid date') {
    const endTime = moment(time).add(amount, unit).format(format);
    return {
      start_time: startTime,
      end_time: endTime,
    };
  }

  return {};
};

/**
 * 以结束时间找到开始时间
 * @param endTime    结束时间
 * @param amount  时间跨度数量
 * @param unit    时间跨度计算的单位
 * @returns
 */
export const getTimeSlotByEndTime = (
  endTime: string,
  amount: DurationInputArg1 = 1,
  unit: unitOfTime.DurationConstructor | undefined = 'day',
  format = 'YYYY-MM-DD HH:mm:ss',
) => {
  const startTime = moment(endTime).subtract(amount, unit).format(format);
  return {
    start_time: startTime,
    end_time: endTime,
  };
};

const dateTipsMap: { [key in TipsType]: TipsMapType | TipsMapFunType } = {
  common: UTCDateFilterTips,
  influencer: influencerActivityDateFilterTips,
  urlCommon: commonUrlDateFilterTips,
  video: UTCVideDateFilterTips
};

/**
 * TODO: 时间过滤器的提示调整,暂时写死，后期优化为活的
 * @param param0
 * @returns
 */
export const getUtcTipsText = ({
  show = true,
  dateTipsKey = 'common',
  fixedTimezone,
}: {
  show?: boolean;
  dateTipsKey?: TipsType;
  fixedTimezone?: number;
}) => {
  if (!show) return null;

  const timezone = fixedTimezone ?? DBO_TM.timezone();

  if (dateTipsKey === 'urlCommon') {
    return commonUrlDateFilterTips(timezone);
  }

  const info = dateTipsMap[dateTipsKey];
  return typeof info === 'function' ? info(timezone) : info;
};

function formatDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 月份是从0开始的，所以加1，并确保两位数
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * TODO: 其他系统使用
 * 计算游戏UTC偏移量，格式化日期格式
 * @param gameInfo
 * @returns
 */
export function getUtcMinusTime(gameInfo: {
  edition_unified_id: string;
}): string {
  const utc = timeZeroGameList.includes(gameInfo.edition_unified_id) ? 0 : 8;

  const localDate = new Date();
  // 获取本地时间的 UTC 时间戳（毫秒）
  const utcTimestamp =
    localDate.getTime() + localDate.getTimezoneOffset() * 60 * 1000;
  // 计算 UTC-8 的时间戳（毫秒）
  const utcMinusEightTimestamp = utcTimestamp + utc * 60 * 60 * 1000;
  return formatDate(new Date(utcMinusEightTimestamp));
}

/**
 * TODO: 其他系统使用
 * 判断A日期是否在B日期后
 * @param A
 * @param B
 * @returns
 */
export function momentTimeAAfterB(A: string, B: string) {
  return moment(A).isAfter(moment(B));
}

export const getChartTimeParams = (
  timeString: string,
  dataType: ServerTimeType,
) => {
  if (dataType === 'hourly') {
    return {
      start_time: moment(timeString)
        .startOf('hour')
        .format('YYYY-MM-DD HH:mm:ss'),
      end_time: moment(timeString).endOf('hour').format('YYYY-MM-DD HH:mm:ss'),
    };
  }
  if (dataType === 'weekly') {
    return {
      start_time: moment(timeString)
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
      end_time: moment(timeString)
        .add(6, 'days')
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
    };
  }

  if (dataType === 'monthly') {
    return {
      start_time: moment(timeString)
        .startOf('month')
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
      end_time: moment(timeString)
        .endOf('month')
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
    };
  }

  return {
    start_time: moment(timeString).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    end_time: moment(timeString).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  };
};

/**
 * 获取过滤器默认时间
 * @param start  当前日期减去多少天作为开始时间 默认是前一天
 * @param end 当前日期减去多少天作为结束时间 默认是前一天
 * @returns
 */
export function getFilterDefaultDay(start = 1, end = 1) {
  const start_time = moment()
    .subtract(start, 'day')
    .startOf('day')
    .format('YYYY-MM-DD HH:mm:ss');
  const end_time = moment()
    .subtract(end, 'day')
    .endOf('day')
    .format('YYYY-MM-DD HH:mm:ss');
  return {
    start_time,
    end_time,
  };
}

/**
 * 格式化当天使用小时方式，其他按原格式方式
 * @param filter
 * @returns
 */
export function formatSameDayFunc(filter: any) {
  if (filter.date_type === 'monthly') {
    return formatDateFunc('daily');
  }
  if (
    filter.date_type === 'daily' &&
    moment(filter.start_time).isSame(moment(), 'day') &&
    moment(filter.end_time).isSame(moment(), 'day')
  ) {
    return formatDateFunc('hourly');
  }
  return formatDateFunc(filter.date_type);
}

/**
 * 比较两个时间点，判断第一个时间点是否在第二个时间点之前
 *
 * @param time1 {string} 第一个时间点的字符串表示
 * @param time2 {string} 第二个时间点的字符串表示
 * @returns {boolean} 如果第一个时间点在第二个时间点之前，则返回true；否则返回false
 */
export function comparableTime(time1: string, time2: string) {
  return moment(time1).isBefore(moment(time2));
}

/**
 * 获取选中的数据是否为今天
 * @param filter
 * @returns
 */
export function getIsToday(filter: any) {
  let { start_time, end_time } = filter;
  const time = getUtcTodayTime();
  const isToday =
    start_time?.substring(0, 10) === end_time?.substring(0, 10) &&
    end_time?.substring(0, 10) === time.format('YYYY-MM-DD');

  return isToday;
}

export const formatTimeToServer = (
  time: [string, string],
  dateType: ServerTimeType,
): [string, string] => {
  if (dateType === 'hourly') {
    return time;
  }

  const ofText = dateType === 'monthly' ? 'month' : 'day';

  return time.map((item, index) => {
    const funcStr: 'startOf' | 'endOf' = index === 0 ? 'startOf' : 'endOf';
    return moment(item)
    [funcStr](ofText)
    [funcStr]('hour')
      .format('YYYY-MM-DD HH:mm:ss');
  }) as [string, string];
};

export const formatTimeToObjServer = (
  time: [string, string],
  dateType: ServerTimeType,
): { start_time: string; end_time: string } => {
  const [start_time, end_time] = formatTimeToServer(time, dateType);
  return { start_time, end_time };
};

/**
 * 返回当前时区utc值
 * utc 0 =》 0
 * utc 8 =》 8
 * @returns
 */
export function getTimeZoneUtc() {
  let timezone = Number(DBO_FWC.get('timezone'));

  if (isNaN(timezone)) {
    timezone = 0;
  }
  return 8 + timezone;
}

export function getUtcTodayTime() {
  const utcValue = getTimeZoneUtc();
  if (utcValue === 0) return moment.utc();
  return moment.utc().utcOffset(utcValue);
}

export function getUserUtcTime(utc = 8) {
  return moment.utc().utcOffset(utc);
}

export function getTodayTime(dateType: ServerTimeType) {
  const time = getUtcTodayTime().format(allTimeFormatObj[dateType]);
  const date = formatTimeToServer([time, time], dateType);

  return {
    start_time: date[0],
    end_time: date[1],
  };
}

export function getFilterTodayTime(dateType: ServerTimeType) {
  let utcTime = getUtcTodayTime();
  const filterRollbackHours = Number(
    DBO_FWC.get('default_filter_rollback_hours'),
  );
  if (utcTime.get('hour') < filterRollbackHours) {
    utcTime = utcTime.subtract(1, 'day');
  }
  const time = utcTime.format(allTimeFormatObj[dateType]);
  const date = formatTimeToServer([time, time], dateType);

  return {
    start_time: date[0],
    end_time: date[1],
  };
}

/**
 * 以utc时间获取过滤器默认时间
 * @param start  当前日期减去多少天作为开始时间 默认是前一天
 * @param end 当前日期减去多少天作为结束时间 默认是前一天
 * @returns
 */
export function getUtcFilterDefaultDay(start = 1, end = 1) {
  const start_time = getUtcTodayTime()
    .subtract(start, 'day')
    .startOf('day')
    .format('YYYY-MM-DD HH:mm:ss');
  const end_time = getUtcTodayTime()
    .subtract(end, 'day')
    .endOf('day')
    .format('YYYY-MM-DD HH:mm:ss');
  return { start_time, end_time };
}

/**
 * 获取最近完整周的时间范围（周一到周日）
 * 如果当前周已完整（今天是周日），返回当前周（周一到周日）
 * 否则返回上一周（周一到周日）
 * @returns { start_time, end_time }
 */
export function getUtcFilterDefaultWeek() {
  const now = getUtcTodayTime();
  const currentIsoDayOfWeek = now.isoWeekday(); // 1(周一) 到 7(周日)

  // 判断本周是否完整（周日为一周的最后一天）
  const isCurrentWeekComplete = currentIsoDayOfWeek === 7;

  // 获取目标周的周一和周日
  const targetWeek = isCurrentWeekComplete ? now : now.subtract(1, 'week');

  const weekStart = targetWeek
    .startOf('isoWeek')
    .startOf('day')
    .format('YYYY-MM-DD HH:mm:ss');
  const weekEnd = targetWeek
    .endOf('isoWeek')
    .endOf('day')
    .format('YYYY-MM-DD HH:mm:ss');

  return { start_time: weekStart, end_time: weekEnd };
}

/**
 * 当日期为当天的时候自动向前推多少天的方法
 * @param filter
 * @param subtractParams
 * @returns
 */
export function getIsTodayTime(
  filter: any,
  subtractParams: [
    amount?: DurationInputArg1,
    unit?: unitOfTime.DurationConstructor | undefined,
  ] = [30, 'days'],
  format = 'YYYY-MM-DD HH:mm:ss',
) {
  const { date_type, start_time, end_time } = filter;
  if (
    date_type === 'daily' &&
    moment(start_time).format('YYYY-MM-DD') ===
    moment(end_time).format('YYYY-MM-DD')
  ) {
    const start_time = getUtcTodayTime()
      .subtract(...subtractParams)
      .startOf('day')
      .format(format);
    const end_time = getUtcTodayTime().endOf('day').format(format);
    return { ...filter, start_time, end_time };
  }

  return filter;
}

export function getReportSuffix() {
  return '';
  return `(Local Time${moment().format('YYYY-MM-DD HH:mm:ss')})`;
}

export function roundDownMinutes(date: string, step = 5) {
  const newDate = new Date(date);
  const minutes = newDate.getMinutes();
  const remainder = minutes % step;

  // 减去多余的分钟数，并将秒和毫秒归零
  newDate.setMinutes(minutes - remainder);
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);

  return moment(newDate).format('YYYY-MM-DD HH:mm:ss');
}

export function addTimeToDateTime(dateTime: string, timeToAdd: string): string {
  const date = moment(dateTime);
  const [hours, minutes] = timeToAdd.split(':').map(Number);
  date.add(hours, 'hours').add(minutes, 'minutes');
  return date.format('YYYY-MM-DD HH:mm:ss');
}

export function subtractTimes(time1: string, time2: string): string {
  const m1 = moment(time1, 'YYYY-MM-DD HH:mm:ss');
  const m2 = moment(time2, 'YYYY-MM-DD HH:mm:ss');
  const duration = moment.duration(m1.diff(m2));
  return `${duration.hours()}:${String(duration.minutes()).padStart(2, '0')}`;
}

/**
 * Compare two dates and return the later timestamp.
 * If they are the same day, return the daily date at 23:59:59.
 * Handles invalid or undefined inputs safely.
 *
 * @param commentTime - The comment time in "YYYY-MM-DD HH:mm:ss" format.
 * @param dailyDate - The daily date in "YYYY-MM-DD" format.
 * @param unit - The unit to consider for "same day" comparison (default is "day").
 * @returns A timestamp string in "YYYY-MM-DD HH:mm:ss" format, or null if both invalid.
 */
export function getLatestTimestamp(
  commentTime?: string,
  dailyDate?: string,
  unit: moment.unitOfTime.StartOf = 'day',
): string | null {
  // Parse inputs safely
  const commentMoment = commentTime
    ? moment(commentTime, 'YYYY-MM-DD HH:mm:ss', true)
    : null;

  const dailyMoment = dailyDate ? moment(dailyDate, 'YYYY-MM-DD', true) : null;

  // Handle invalid dates
  const isCommentValid = commentMoment?.isValid?.() ?? false;
  const isDailyValid = dailyMoment?.isValid?.() ?? false;

  if (!isCommentValid && !isDailyValid) {
    // Both invalid → return null
    return null;
  }

  // If only one is valid, return that one (end of day if it's dailyDate)
  if (!isCommentValid && isDailyValid) {
    return dailyMoment!.endOf(unit).format('YYYY-MM-DD HH:mm:ss');
  }

  if (isCommentValid && !isDailyValid) {
    return commentMoment!.format('YYYY-MM-DD HH:mm:ss');
  }

  // Both valid → proceed with comparison
  let result: moment.Moment;

  if (commentMoment!.isSame(dailyMoment!, unit)) {
    // Same unit (e.g. same day → take end of that unit)
    result = dailyMoment!.endOf(unit);
  } else if (commentMoment!.isAfter(dailyMoment!)) {
    // Comment time is later
    result = commentMoment!;
  } else {
    // Daily date is later
    result = dailyMoment!.endOf(unit);
  }

  return result.format('YYYY-MM-DD HH:mm:ss');
}
